/**
 * Saves the current URL path and search params to sessionStorage.
 * Used for preserving navigation state across redirects (login, company switch).
 *
 * @param {string} key - The sessionStorage key to use
 */
export const saveCurrentRoute = (key) => {
  if (!key) return

  const { pathname, search } = window.location

  const hasPath = pathname && pathname !== '/'
  const hasSearch = search && search !== '?'

  if (!hasPath && !hasSearch) return

  const value = [ pathname, search ].join('')

  try {
    sessionStorage.setItem(key, value)
  } catch {
    // fail silently - route persistence is non-critical
    // can fail in private mode, quota exceeded, or restricted iframe
  }
}

/**
 * Retrieves and clears a saved route from sessionStorage.
 * Use this when you need the route value for custom navigation logic
 * (e.g., Vue Router's next() function).
 *
 * @param {string} key - The sessionStorage key to read from
 * @returns {string|null} - The saved route or null if not found
 */
export const getSavedRoute = (key) => {
  if (!key) return null

  try {
    const value = sessionStorage.getItem(key)
    sessionStorage.removeItem(key)
    return value || null
  } catch {
    // fail silently - can occur in restricted contexts
    return null
  }
}

/**
 * Restores navigation to a previously saved route from sessionStorage.
 * Performs a full page redirect. The route is removed from storage after retrieval.
 *
 * @param {string} key - The sessionStorage key to read from
 * @param {string} baseUrl - The base URL to prepend to the path
 * @returns {boolean} - True if a redirect was performed
 */
export const restoreSavedRoute = (key, baseUrl) => {
  const value = getSavedRoute(key)

  if (value && baseUrl) {
    window.location.href = [ baseUrl, value ].join('')
    return true
  }

  return false
}
